import React from 'react';
import {
  Box, Paper, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Divider, CircularProgress, Stack, ToggleButtonGroup, ToggleButton,
  Collapse, IconButton, Chip, Typography, Tooltip, Popover, List, ListItemButton, ListItemText,
  Dialog, DialogTitle, DialogContent, MenuItem, LinearProgress
} from '@mui/material';
import { Download, GridView, Search, Close } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReactApexChart from 'react-apexcharts';
import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

// --------- helpers ---------
const fmtQty = (n) => (n==null?'-':Number(n).toLocaleString(undefined,{maximumFractionDigits:0}));
const fmtMoney0 = (n) => (n==null?'-':Number(n).toLocaleString(undefined,{maximumFractionDigits:0}));
const fmtMoney2 = (n) => (n==null?'-':Number(n).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2}));
const toCSV = (rows) => rows.map(r => r.map(x => `"${String(x ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
const impactSx = (v) => ({
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  color: v > 0 ? 'success.main' : (v < 0 ? 'error.main' : 'text.primary'),
  fontWeight: v !== 0 ? 600 : 700, // keep head bold too
});
const downloadCSV = (filename, rows) => {
  const csv = "\uFEFF" + toCSV(rows);
  try {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.style.display='none'; a.href=url; a.setAttribute('download', filename);
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(()=>URL.revokeObjectURL(url),0);
  } catch (e) {
    const uri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); window.open(uri, '_blank');
  }
};
const sanitize = (s='') => String(s).toLowerCase().replace(/[^a-z0-9\-_.]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'');

// Period helpers (start at 2024-01-01)
const START_LIMIT = { y: 2024, m: 1 };
const NOW = new Date();
const TODAY_Y = NOW.getFullYear();
const TODAY_M = NOW.getMonth() + 1;

function yearsFrom(startYear = 2024){
  const y = new Date().getFullYear();
  const out = [];
  for(let i=startYear;i<=y;i++) out.push(i);
  return out;
}
function monthsList(){ return Array.from({length:12},(_,i)=>i+1); }
function clampFutureMonth(y, m){
  if(y > TODAY_Y) return { y: TODAY_Y, m: TODAY_M };
  if(y === TODAY_Y && m > TODAY_M) return { y, m: TODAY_M };
  return {y, m};
}
function readableMonth(y, m){ return `${y}-${String(m).padStart(2,'0')}`; }
function isoWeekStr(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  const y = date.getUTCFullYear();
  const w = String(weekNo).padStart(2, '0');
  return `${y}-W${w}`;
}
function currentIsoWeekParts(){
  const s = isoWeekStr(new Date());
  const [yPart, wPart] = s.split('-W');
  return { y: Number(yPart), w: Number(wPart) };
}
function weekOfYearList(){ return Array.from({length:53},(_,i)=>i+1); }
function isoWeekStrFromParts(y, w){ return `${y}-W${String(w).padStart(2,'0')}`; }

// --------- API root ---------
const API_URL_PVM = BackofficeReportsApi.getMarginsPvmUrl();
const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';

// --------- Small reusable filter chip for Location only ---------
function ChipPicker({ label, options, value, onChange }) {
  const [anchor, setAnchor] = React.useState(null);
  const [q, setQ] = React.useState('');
  const open = Boolean(anchor);
  const filtered = (options || []).filter(o => !q || (o.label || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Chip
        size="small"
        color="default"
        variant="outlined"
        label={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', minWidth: 0 }}>
            <GridView sx={{ fontSize: 14, flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: 130, sm: 180 },
              }}
            >
              {label}
            </Box>
          </Box>
        }
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          height: 36,
          borderRadius: '12px',
          color: '#1a237e',
          bgcolor: 'rgba(26, 35, 126, 0.06)',
          borderColor: 'rgba(26, 35, 126, 0.14)',
          fontWeight: 650,
          maxWidth: { xs: '100%', sm: 230 },
          '&:hover': {
            bgcolor: 'rgba(26, 35, 126, 0.10)',
          },
          '& .MuiChip-label': {
            minWidth: 0,
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQ('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.75,
            borderRadius: '14px',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.16)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 280 }}>
          <Search fontSize="small" sx={{ color: '#64748b' }} />
          <TextField
            size="small"
            placeholder={`Search ${label.toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                height: 36,
                borderRadius: '12px',
                fontSize: '0.86rem',
              },
            }}
          />
          <Button
            size="small"
            onClick={() => {
              onChange(options[0].value);
              setAnchor(null);
              setQ('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
            }}
          >
            Clear
          </Button>
        </Box>

        <Divider />

        <List dense sx={{ overflowY: 'auto', maxHeight: 300, minWidth: 280, py: 0.5 }}>
          {filtered.map(o => (
            <ListItemButton
              key={o.value}
              dense
              selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setAnchor(null);
                setQ('');
              }}
              sx={{
                mx: 0.5,
                borderRadius: '10px',
                '&.Mui-selected': {
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                  color: '#1a237e',
                },
              }}
            >
              <ListItemText
                primaryTypographyProps={{
                  noWrap: true,
                  fontSize: '0.86rem',
                  fontWeight: o.value === value ? 750 : 500,
                }}
                primary={o.label}
              />
            </ListItemButton>
          ))}

          {!filtered.length && (
            <ListItemButton disabled dense>
              <ListItemText primary="No matches" />
            </ListItemButton>
          )}
        </List>
      </Popover>
    </>
  );
}

export default class InventoryPVMAnalysisPage extends React.Component{
  state = {
    client: new Client('none'),

    // filters
    open: true,
    mode: 'month', // 'month' | 'week'
    yearMonth: { y: Math.max(TODAY_Y, START_LIMIT.y), m: Math.min(TODAY_M, 12) },
    yearWeek: currentIsoWeekParts(),
    wtd: true,      // active only if current week selected

    compare: 'prev', // 'prev' | 'ly'
    groupBy: 'product', // 'product' | 'category'
    productOpts: [{ value:'__ALL__', label:'All Products' }], productSel: '__ALL__',
    locationOpts: [{ value:'__ALL__', label:'All Locations' }], locationSel: '__ALL__',
    deliverySel: '',

    // data
    loading: false,
    busyOverlay: false,
    rows: [],
    totals: null,
    meta: null,

    // UI
    lens: 'pvmMargin', // kept for compatibility
    listOpen: false,
    clickedGroupKey: null,
    search: '',
    summary: '',
    vw: (typeof window !== 'undefined' ? window.innerWidth : 1280),
    vh: (typeof window !== 'undefined' ? window.innerHeight : 800),
  };

  componentDidMount(){
    this.bootstrap();
    this._onResize = () => this.setState({ vw: window.innerWidth, vh: window.innerHeight });
    window.addEventListener('resize', this._onResize);
  }

  componentWillUnmount(){
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  isSmallScreen(){
    const { vw, vh } = this.state;
    return vw <= 900 || vh <= 650; // tuned for 800×600 friendliness
  }

  async bootstrap(){
    await new Promise(resolve =>
      this.setState({ loading: true, busyOverlay: true }, resolve)
    );
    // give the browser a frame (or two) to paint the overlay
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    const _shownAt = performance.now();
    try{
      const client = this.state.client;
      const [pRes, lRes] = await Promise.all([
        client.getData(API_URL_PRODUCTS, { isForSale: 1, orderBy_name: true }) || {},
        client.getData(API_URL_LOCATIONS, { status: 1 }) || {},
      ]);
      const products = (pRes?.products) || pRes?.results || pRes || [];
      const locations = (lRes?.locations) || lRes?.results || lRes || [];
      // sort products alphabetically
      const productOpts = [{ value:'__ALL__', label:'All Products' },
        ...products.map(p=>({ value:String(p.iref||p.id), label:p.name })).sort((a,b)=>a.label.localeCompare(b.label))];
      const locationOpts = [{ value:'__ALL__', label:'All Locations' },
        ...locations.map(l=>({ value:String(l.iref||l.id), label:l.name })).sort((a,b)=>a.label.localeCompare(b.label))];
      // keep overlay visible a touch, like SalesCountOrders
      const MIN_VISIBLE = 400;
      const shownFor = performance.now() - _shownAt;
      if (shownFor < MIN_VISIBLE) await new Promise(r => setTimeout(r, MIN_VISIBLE - shownFor));
      this.setState({ productOpts, locationOpts, loading:false, busyOverlay:false }, this.propagateSummary);
    }catch{
      const MIN_VISIBLE = 400;
      const shownFor = performance.now() - _shownAt;
      if (shownFor < MIN_VISIBLE) await new Promise(r => setTimeout(r, MIN_VISIBLE - shownFor));
      this.setState({ loading:false, busyOverlay:false });
    }
  }

  get period(){
    if(this.state.mode === 'month'){
      const clamped = clampFutureMonth(this.state.yearMonth.y, this.state.yearMonth.m); // block future enforced
      return readableMonth(clamped.y, clamped.m);
    }
    return isoWeekStrFromParts(this.state.yearWeek.y, this.state.yearWeek.w); // e.g. 2025-W41
  }

  isCurrentWeek(){
    if (this.state.mode !== 'week') return false;
    return isoWeekStr(new Date()) === isoWeekStrFromParts(this.state.yearWeek.y, this.state.yearWeek.w);
  }

  propagateSummary = () => {
    const { mode, compare, groupBy, locationSel, deliverySel, locationOpts, yearMonth, yearWeek } = this.state;
    const locLabel = (locationOpts.find(o=>o.value===locationSel)?.label)||'All Locations';
    const pLabel = mode==='month' ? `Month ${readableMonth(yearMonth.y, yearMonth.m)}` : `Week ${isoWeekStrFromParts(yearWeek.y, yearWeek.w)}`;
    const summary = [
      pLabel,
      compare === 'prev' ? 'vs previous' : 'vs last year',
      groupBy === 'category' ? 'by Category' : 'by Product',
      locLabel,
      (deliverySel === '1' ? 'Delivery only' : (deliverySel === '0' ? 'No delivery' : 'Any'))
    ].join(' · ');
    this._draftSummary = summary;
  };

  buildPayload(){
    const { mode, compare, groupBy, locationSel, productSel, deliverySel, wtd } = this.state;
    const params = {
      mode, // month|week
      period: this.period,
      compare, // prev|ly
      groupBy, // product|category (backend param name)
    };
    if (mode === 'week' && this.isCurrentWeek() && wtd) params.wtd = 1;
    if (locationSel !== '__ALL__') params.location = locationSel;
    // backend expects 'products' (plural). If you add multi-select later, send CSV here.
    if (productSel !== '__ALL__') params.products = productSel;
    if (deliverySel === '0' || deliverySel === '1') params.delivery = deliverySel;
    const summaryLabel = this._draftSummary || '';
    return { params, summaryLabel };
  }

  apply = () => {
    const payload = this.buildPayload();
    this.fetch(payload);
  }

  async fetch(payload){
    if(!payload) return;
    const { params, summaryLabel } = payload;
    await new Promise(resolve =>
      this.setState({ loading: true, busyOverlay: true, summary: summaryLabel }, resolve)
    );
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    const _shownAt = performance.now();
    try{
      const data = await this.state.client.getData(API_URL_PVM, params) || {};
      const rows = (data.rows || []).map((r,i)=>({ id:i, ...r }));
      const totals = data.totals || null;
      const meta = data.meta || null;
      const MIN_VISIBLE = 400;
      const shownFor = performance.now() - _shownAt;
      if (shownFor < MIN_VISIBLE) await new Promise(r => setTimeout(r, MIN_VISIBLE - shownFor));
      this.setState({ rows, totals, meta, loading:false, busyOverlay:false });
    }catch(e){
      const MIN_VISIBLE = 400;
      const shownFor = performance.now() - _shownAt;
      if (shownFor < MIN_VISIBLE) await new Promise(r => setTimeout(r, MIN_VISIBLE - shownFor));
      this.setState({ loading:false, busyOverlay:false });
    }
  }

  // ----- CHART: Margin0 -> impacts -> Margin1 (new decomposition) -----
  waterfallSeries(){
    const dec = this.state.totals?.decomposition || {};
    const M0 = Math.round(this.state.totals?.margin0 || 0);
    const M1 = Math.round(this.state.totals?.margin1 || 0);

    const priceImpact = Math.round(dec.priceImpact || 0);
    const volMix      = Math.round(dec.volMix || 0);
    const cogsImpact  = Math.round(dec.cogsImpact || 0);

    const order = [
      ['Margin 0', M0],
      ['Net Price', priceImpact],
      ['Vol/Mix', volMix],
      ['COGS', cogsImpact],
      ['Margin 1', M1]
    ];

    const series = [{
      name: 'Δ',
      data: order.map(([name, val]) => {
        const isTotal = name === 'Margin 0' || name === 'Margin 1';
        const color = isTotal ? '#2ca6d8' : (val >= 0 ? '#2ea043' : '#d73a49');
        return { x: name, y: val, fillColor: color };
      })
    }];

    const options = {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        animations: { enabled: false },
      },
      plotOptions: {
        bar: {
          distributed: true,
          dataLabels: { position: 'top' },   // labels outside the bars
          columnWidth: '38%',
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => (val ?? 0).toLocaleString(),
        style: { fontSize: '12px', fontWeight: 700, colors: ['#111'] }, // plain dark text
        offsetY: -10,                         // float above the column
        background: { enabled: false },       // ⟵ no chip!
        dropShadow: false
      },
      grid: { padding: { top: 16, bottom: 24 } }, // room so near-zero values don’t clip
      xaxis: { type: 'category', labels: { rotate: -15 } },
      yaxis: {
        forceNiceScale: true,
        labels: { formatter: (v) => (v ?? 0).toLocaleString() }
      },
      tooltip: { y: { formatter: (val) => (val ?? 0).toLocaleString() } },
      legend: { show: false },
    };
    return { series, options };
  }

  // ----- CHART: Top drivers (Net Price, Vol/Mix, COGS) -----
  topDrivers(){
    const { rows } = this.state;
    if (!rows || rows.length === 0) {
      // Safe fallback – empty chart config
      return {
        series: [],
        options: {
          chart: { type: 'bar', stacked: true, toolbar: { show: false } },
          plotOptions: { bar: { horizontal: true } },
          xaxis: { categories: [] },
        },
      };
    }

    const top = [...rows]
      .sort(
        (a, b) =>
          Math.abs((b.margin1 || 0) - (b.margin0 || 0)) -
          Math.abs((a.margin1 || 0) - (a.margin0 || 0))
      )
      .slice(0, 15);

    const names  = top.map(r => r.groupName);
    const price  = top.map(r => Math.round(r.decomposition?.priceImpact || 0));
    const volmix = top.map(r => Math.round(r.decomposition?.volMix || 0));
    const cogs   = top.map(r => Math.round(r.decomposition?.cogsImpact || 0));

    const series = [
      { name: 'Net Price', data: price },
      { name: 'Vol/Mix',   data: volmix },
      { name: 'COGS',      data: cogs },
    ];

    const allValues = [...price, ...volmix, ...cogs];
    // absolute max impact
    let maxAbs = Math.max(...allValues.map(v => Math.abs(v || 0)));
    if (!isFinite(maxAbs) || maxAbs === 0) maxAbs = 1;
    // padding so bars don't touch edges
    maxAbs *= 1.1;
    // 🎯 Round to nearest nice step (1k / 5k / 10k / etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxAbs)));
    let step = magnitude;
    // if numbers are big, bump step for cleaner ticks
    if (maxAbs / step > 5) step *= 5;
    else if (maxAbs / step > 2) step *= 2;
    const domainMax = Math.ceil(maxAbs / step) * step;
    const domainMin = -domainMax;

    const options = {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      plotOptions: {
        bar: { horizontal: true },
      },
      dataLabels: { enabled: false },

      xaxis: {
        categories: names,
        min: domainMin,
        max: domainMax,
        tickAmount: 5, // ⬅️ fewer numeric labels (try 4–5)
        labels: {
          formatter: (v) => (v ?? 0).toLocaleString(),
        },
      },

      legend: { position: 'top' },
      tooltip: {
        y: { formatter: (v) => (v ?? 0).toLocaleString() },
      },
    };

    return { series, options };
  }

  // ----- CSV for modal table (with filter summary) -----
  exportCSV = () => {
    const { rows, clickedGroupKey, search, summary, totals, meta } = this.state;
    const base = clickedGroupKey ? rows.filter(r => r.groupKey === clickedGroupKey) : rows;

    // sort by product/category name
    const baseSorted = [...base].sort((a,b)=> (a.groupName||'').localeCompare(b.groupName||''));

    const filtered = baseSorted.filter(r => !search || (r.groupName||'').toLowerCase().includes(search.toLowerCase()));

    const head = ['Name','Total Δ','Net Price Δ','Vol/Mix Δ','COGS Δ','Q0','Q1','Net Price0','Net Price1','Cost0','Cost1','VAT%'];
    const listRows = filtered.map(r => {
      const dec = r.decomposition || {};
      const delta = Math.round((r.margin1 || 0) - (r.margin0 || 0));
      return [
        r.groupName, delta, 
        Math.round(dec.priceImpact || 0),
        Math.round(dec.volMix || 0),
        Math.round(dec.cogsImpact || 0),
        r.q0, r.q1,
        // two-decimals for unit prices & costs
        (r.p0!=null? Number(r.p0).toFixed(2):''),
        (r.p1!=null? Number(r.p1).toFixed(2):''),
        (r.u0!=null? Number(r.u0).toFixed(2):''),
        (r.u1!=null? Number(r.u1).toFixed(2):''),
        r.vatPercent ?? ''
      ];
    });

    const pCur = meta?.periods?.current ? `${meta.periods.current.from} to ${meta.periods.current.to}` : '';    // ASCII arrow fix
    const pBase = meta?.periods?.baseline ? `${meta.periods.baseline.from} to ${meta.periods.baseline.to}` : '';
    const locationLabel = (this.state.locationOpts.find(o=>o.value===this.state.locationSel)?.label) || 'All Locations';

    // Totals & unit margins at end
    const tM0 = Math.round(totals?.margin0 || 0);
    const tM1 = Math.round(totals?.margin1 || 0);
    const tQ0 = totals?.q0 || 0;
    const tQ1 = totals?.q1 || 0;
    const tUM0 = tQ0>0 ? (totals.margin0 / tQ0).toFixed(2) : '';
    const tUM1 = tQ1>0 ? (totals.margin1 / tQ1).toFixed(2) : '';

    const preface = [
      ['PVM Export'],
      ['Summary', summary || ''],
      ['Current Window', pCur],
      ['Baseline Window', pBase],
      [],
    ];

    const totalsBlock = [
      [],
      ['Totals'],
      ['Total Margin 0', tM0],
      ['Total Margin 1', tM1],
      ['Unit Margin 0 (M0/Q0)', tUM0],
      ['Unit Margin 1 (M1/Q1)', tUM1],
    ];

    // Unique filename
    const fname = [
      'pvm',
      this.state.mode,
      this.period,
      this.state.compare,
      this.state.groupBy,
      sanitize(locationLabel || 'all-locations'),
    ].filter(Boolean).join('_') + '.csv';

    downloadCSV(fname, [ ...preface, head, ...listRows, ...totalsBlock ]);
  };

  renderHeaderBar() {
    const s = this.state;

    return (
      <Box
        sx={{
          px: { xs: 0.75, sm: 1 },
          py: { xs: 0.65, sm: 0.85 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 0.75, sm: 1 },
          flexWrap: 'nowrap',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 0.75 },
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: '#1a237e',
              fontWeight: 800,
              fontSize: { sm: '0.92rem', md: '1rem' },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 0,
            }}
          >
            PVM Margin
          </Typography>

          <Tooltip title="Hide/Show filters">
            <IconButton
              onClick={() => this.setState({ open: !s.open })}
              size="small"
              aria-label="filters"
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '12px',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                color: s.open ? '#1a237e' : '#64748b',
                bgcolor: s.open ? 'rgba(26, 35, 126, 0.08)' : '#fff',
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.12)',
                },
              }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Typography
            sx={{
              color: '#64748b',
              fontWeight: 650,
              fontSize: { xs: '0.76rem', sm: '0.82rem' },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {this._draftSummary || s.summary || ''}
          </Typography>
        </Box>

        <Tooltip title="Export CSV">
          <Button
            size="small"
            variant="contained"
            startIcon={<Download fontSize="small" />}
            onClick={this.exportCSV}
            sx={{
              minWidth: 36,
              width: { xs: 36, sm: 'auto' },
              height: 36,
              px: { xs: 0, sm: 1.1 },
              flexShrink: 0,
              borderRadius: '12px',
              color: '#1a237e',
              bgcolor: 'rgba(26, 35, 126, 0.08)',
              boxShadow: 'none',
              border: '1px solid rgba(26, 35, 126, 0.12)',
              textTransform: 'none',
              fontWeight: 750,
              '&:hover': {
                bgcolor: 'rgba(26, 35, 126, 0.12)',
                boxShadow: 'none',
              },
              '& .MuiButton-startIcon': {
                m: { xs: 0, sm: '0 4px 0 -2px' },
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              CSV
            </Box>
          </Button>
        </Tooltip>
      </Box>
    );
  }

  render(){
    const s = this.state;
    const { series: wfSeries, options: wfOptions } = this.waterfallSeries();
    const { series: tdSeries, options: tdOptions } = this.topDrivers();
    const isSmall = this.isSmallScreen();
    const wfH = isSmall ? (s.open ? 260 : 320) : (s.open ? 330 : 360);   // waterfall chart
    const tdH = isSmall ? (s.open ? 300 : 360) : 420;                    // top drivers
    const dialogW = isSmall ? '96vw' : '80vw';
    const dialogH = isSmall ? '80vh' : '72vh';
    const tableMaxH = isSmall ? 'calc(80vh - 200px)' : 'calc(72vh - 200px)';

    const listBase = s.clickedGroupKey ? s.rows.filter(r=>r.groupKey===s.clickedGroupKey) : s.rows;
    const filteredRows = listBase.filter(r => !s.search || (r.groupName||'').toLowerCase().includes(s.search.toLowerCase()));
    // Subtotals for rows currently visible in the modal (after search / clicked group)
    const selectedAgg = filteredRows.reduce((acc, r) => {
      const dec = r.decomposition || {};
      acc.delta  += Math.round((r.margin1 || 0) - (r.margin0 || 0));
      acc.price  += Math.round(dec.priceImpact || 0);
      acc.volmix += Math.round(dec.volMix || 0);
      acc.cogs   += Math.round(dec.cogsImpact || 0);
      acc.q0     += Number(r.q0 || 0);
      acc.q1     += Number(r.q1 || 0);
      return acc;
    }, { delta: 0, price: 0, volmix: 0, cogs: 0, q0: 0, q1: 0 });

    const totalsDec = s.totals?.decomposition || {};
    const deltaMarginTotal = Math.round((s.totals?.margin1 || 0) - (s.totals?.margin0 || 0));

    return (
      <Paper
        sx={{
          position: 'relative',
          width: '100%',
          height: {
            xs: 600,
            sm: '100%',
          },
          maxHeight: {
            xs: 600,
            sm: '100%',
          },
          minHeight: 0,
          borderRadius: { xs: '14px', sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: '#fff',
        }}
      >
        {this.renderHeaderBar()}

        {this.state.loading && (
          <LinearProgress
            sx={{
              height: 3,
              flexShrink: 0,
              '& .MuiLinearProgress-bar': {
                transition: 'none',
              },
            }}
          />
        )}

        <Collapse in={s.open} timeout="auto" sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              px: { xs: 0.75, sm: 1 },
              py: { xs: 0.75, sm: 1 },
              bgcolor: '#fff',
              borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
              maxHeight: {
                xs: 150,
                sm: 124,
                md: 124,
                lg: 124,
              },
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(15, 23, 42, 0.18)',
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1 },
                pb: 0.25,

                '& .MuiToggleButtonGroup-root': {
                  borderRadius: '12px',
                  overflow: 'hidden',
                  bgcolor: '#fff',
                },
                '& .MuiToggleButton-root': {
                  height: 36,
                  px: { xs: 1, sm: 1.25 },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                  color: '#64748b',
                  borderColor: 'rgba(15, 23, 42, 0.10)',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  color: '#1a237e',
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                },
                '& .MuiInputBase-root': {
                  height: 36,
                  borderRadius: '12px',
                  fontSize: { xs: '0.82rem', sm: '0.88rem' },
                  bgcolor: '#fff',
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                },
              }}
            >
              <ToggleButtonGroup
                value={s.mode}
                exclusive
                onChange={(_e, v) => {
                  if (!v) return;
                  this.setState({ mode: v }, this.propagateSummary);
                }}
                size="small"
              >
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                value={s.compare}
                exclusive
                onChange={(_e, v) => {
                  if (!v) return;
                  this.setState({ compare: v }, this.propagateSummary);
                }}
                size="small"
              >
                <ToggleButton value="prev">Previous</ToggleButton>
                <ToggleButton value="ly">Last year</ToggleButton>
              </ToggleButtonGroup>

              {s.mode === 'month' ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Year"
                    value={s.yearMonth.y}
                    onChange={(e) =>
                      this.setState(
                        { yearMonth: { ...s.yearMonth, y: Number(e.target.value) } },
                        this.propagateSummary
                      )
                    }
                    sx={{ width: { xs: 88, sm: 94 } }}
                  >
                    {yearsFrom(START_LIMIT.y).map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Month"
                    value={s.yearMonth.m}
                    onChange={(e) =>
                      this.setState(
                        { yearMonth: { ...s.yearMonth, m: Number(e.target.value) } },
                        this.propagateSummary
                      )
                    }
                    sx={{ width: { xs: 96, sm: 108 } }}
                  >
                    {monthsList().map(m => (
                      <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Year"
                    value={s.yearWeek.y}
                    onChange={(e) =>
                      this.setState(
                        { yearWeek: { ...s.yearWeek, y: Number(e.target.value) } },
                        this.propagateSummary
                      )
                    }
                    sx={{ width: { xs: 88, sm: 94 } }}
                  >
                    {yearsFrom(START_LIMIT.y).map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>

                  <Tooltip title="After load, see Current/Baseline dates below">
                    <TextField
                      select
                      size="small"
                      label="ISO Week"
                      value={s.yearWeek.w}
                      onChange={(e) =>
                        this.setState(
                          { yearWeek: { ...s.yearWeek, w: Number(e.target.value) } },
                          this.propagateSummary
                        )
                      }
                      sx={{ width: { xs: 104, sm: 120 } }}
                    >
                      {weekOfYearList().map(w => (
                        <MenuItem key={w} value={w}>W{String(w).padStart(2, '0')}</MenuItem>
                      ))}
                    </TextField>
                  </Tooltip>
                </Stack>
              )}

              <ChipPicker
                label={(s.locationOpts.find(o => o.value === s.locationSel)?.label) || 'All Locations'}
                options={s.locationOpts}
                value={s.locationSel}
                onChange={(v) => this.setState({ locationSel: v }, this.propagateSummary)}
              />

              <TextField
                select
                size="small"
                label="Delivery"
                value={s.deliverySel}
                onChange={(e) => this.setState({ deliverySel: e.target.value }, this.propagateSummary)}
                sx={{ width: { xs: 108, sm: 116 } }}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="0">No</MenuItem>
                <MenuItem value="1">Yes</MenuItem>
              </TextField>

              <Button
                size="small"
                variant="contained"
                startIcon={<Search fontSize="small" />}
                onClick={this.apply}
                sx={{
                  height: 36,
                  borderRadius: '12px',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 750,
                  px: 1.25,
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                Load
              </Button>
            </Stack>
          </Box>
        </Collapse>

        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            bgcolor: '#fff',
            p: { xs: 0.75, sm: 1 },
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(15, 23, 42, 0.18)',
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
            },
          }}
        >
          {s.loading ? (
            <Paper
              sx={{
                p: 1,
                mb: 1,
                borderRadius: '14px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  height: isSmall ? (s.open ? 260 : 320) : (s.open ? 330 : 360),
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 1,
                mb: 1,
                borderRadius: '14px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ height: wfH }}>
                <ReactApexChart type="bar" height={wfH} options={wfOptions} series={wfSeries} />
              </Box>

              {this.state.meta?.periods && (
                <Typography
                  sx={{
                    px: 1,
                    color: 'text.secondary',
                    fontSize: { xs: '0.72rem', sm: '0.78rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  Current: {this.state.meta.periods.current.from} to {this.state.meta.periods.current.to} · Baseline: {this.state.meta.periods.baseline.from} to {this.state.meta.periods.baseline.to}
                </Typography>
              )}
            </Paper>
          )}

          {s.loading ? (
            <Paper
              sx={{
                p: 1,
                mb: 1,
                borderRadius: '14px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: 'none',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
                <Typography sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.92rem' }}>
                  Top Drivers
                </Typography>
                <Button size="small" variant="outlined" disabled>Open list</Button>
              </Stack>

              <Box
                sx={{
                  height: isSmall ? (s.open ? 300 : 360) : 420,
                  mt: 1,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 1,
                mb: 1,
                borderRadius: '14px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
                <Typography sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.92rem' }}>
                  Top Drivers
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => this.setState({ listOpen: true })}
                  sx={{
                    height: 34,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Open list
                </Button>
              </Stack>

              <Box sx={{ height: tdH, mt: 1 }}>
                <ReactApexChart type="bar" height={tdH} options={tdOptions} series={tdSeries} />
              </Box>
            </Paper>
          )}
        </Box>

        <Dialog
          fullWidth
          maxWidth="xl"
          open={s.listOpen}
          onClose={() => this.setState({ listOpen: false, clickedGroupKey: null, search: '' })}
          PaperProps={{
            sx: {
              width: dialogW,
              height: dialogH,
              borderRadius: { xs: '14px', sm: '16px' },
              overflow: 'hidden',
            }
          }}
        >
          <DialogTitle
            sx={{
              px: { xs: 1, sm: 1.5 },
              py: { xs: 1, sm: 1.25 },
              borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
              bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: { xs: '0.92rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                Detailed List
              </Typography>

              <Stack direction="row" spacing={0.75} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search…"
                  value={s.search}
                  onChange={(e) => this.setState({ search: e.target.value })}
                  sx={{
                    width: { xs: 130, sm: 220 },
                    '& .MuiInputBase-root': {
                      height: 36,
                      borderRadius: '12px',
                      fontSize: { xs: '0.82rem', sm: '0.88rem' },
                    },
                  }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download fontSize="small" />}
                  onClick={this.exportCSV}
                  sx={{
                    height: 36,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: { xs: 1, sm: 1.25 },
                  }}
                >
                  CSV
                </Button>

                <IconButton
                  color="error"
                  onClick={() => this.setState({ listOpen: false })}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '12px',
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>

          <DialogContent
            sx={{
              p: { xs: 1, sm: 1.5 },
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ mb: 1, flexShrink: 0 }}
            >
              <Chip label={`Δ Margin: ${fmtMoney0(deltaMarginTotal)}`} color="primary" />
              <Chip label={`Net Price Δ: ${fmtMoney0(totalsDec.priceImpact || 0)}`} />
              <Chip label={`Vol/Mix Δ: ${fmtMoney0(totalsDec.volMix || 0)}`} />
              <Chip label={`COGS Δ: ${fmtMoney0(totalsDec.cogsImpact || 0)}`} variant="outlined" />
            </Stack>

            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: 'none',
                overflow: 'auto',
                borderRadius: '14px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: 'none',
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: {
                    xs: 860,
                    sm: 1000,
                    md: 1100,
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                    fontSize: { xs: '0.82rem', sm: '0.88rem' },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      'Name',
                      'Total Δ',
                      'Net Price Δ',
                      'Vol/Mix Δ',
                      'COGS Δ',
                      'Q0',
                      'Q1',
                      'Net Price 0',
                      'Net Price 1',
                      'Cost 0',
                      'Cost 1',
                      'VAT'
                    ].map((h, index) => (
                      <TableCell
                        key={h}
                        align={index === 0 ? 'left' : 'right'}
                        sx={{
                          py: 0.85,
                          px: {
                            xs: 0.75,
                            sm: 1,
                          },
                          bgcolor: '#f8fafc',
                          color: '#1a237e',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          position: index === 0 ? 'sticky' : 'static',
                          left: index === 0 ? 0 : 'auto',
                          zIndex: index === 0 ? 4 : 3,
                          minWidth: index === 0
                            ? {
                                xs: 135,
                                sm: 180,
                                md: 240,
                              }
                            : {
                                xs: 96,
                                sm: 110,
                                md: 'auto',
                              },
                          width: index === 0
                            ? {
                                xs: 135,
                                sm: 180,
                                md: 240,
                              }
                            : {
                                xs: 96,
                                sm: 110,
                                md: 'auto',
                              },
                          maxWidth: index === 0
                            ? {
                                xs: 135,
                                sm: 220,
                                md: 320,
                              }
                            : 'none',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRows.map((r, i) => {
                    const dec = r.decomposition || {};
                    const delta = Math.round((r.margin1 || 0) - (r.margin0 || 0));

                    return (
                      <TableRow key={i} hover>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            bgcolor: '#fff',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: {
                              xs: 135,
                              sm: 180,
                              md: 240,
                            },
                            width: {
                              xs: 135,
                              sm: 180,
                              md: 240,
                            },
                            maxWidth: {
                              xs: 135,
                              sm: 220,
                              md: 320,
                            },
                            fontWeight: 550,
                          }}
                        >
                          {r.groupName}
                        </TableCell>
                        <TableCell align="right" sx={impactSx(delta)}>{fmtMoney0(delta)}</TableCell>
                        <TableCell align="right" sx={impactSx(dec.priceImpact)}>{fmtMoney0(dec.priceImpact || 0)}</TableCell>
                        <TableCell align="right" sx={impactSx(dec.volMix)}>{fmtMoney0(dec.volMix || 0)}</TableCell>
                        <TableCell align="right" sx={impactSx(dec.cogsImpact)}>{fmtMoney0(dec.cogsImpact || 0)}</TableCell>
                        <TableCell align="right">{fmtQty(r.q0)}</TableCell>
                        <TableCell align="right">{fmtQty(r.q1)}</TableCell>
                        <TableCell align="right">{fmtMoney2(r.p0)}</TableCell>
                        <TableCell align="right">{fmtMoney2(r.p1)}</TableCell>
                        <TableCell align="right">{fmtMoney2(r.u0)}</TableCell>
                        <TableCell align="right">{fmtMoney2(r.u1)}</TableCell>
                        <TableCell align="right">{r.vatPercent ?? 0}</TableCell>
                      </TableRow>
                    );
                  })}

                  {!!filteredRows.length && (
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          bgcolor: 'rgba(26, 35, 126, 0.055)',
                          color: '#111827',
                          fontWeight: 900,
                          borderTop: '1px solid rgba(26, 35, 126, 0.12)',
                        },
                        '& .MuiTableCell-root:first-of-type': {
                          color: '#1a237e',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                        }}
                      >
                        TOTAL
                      </TableCell>
                      <TableCell align="right" sx={impactSx(selectedAgg.delta)}>{fmtMoney0(selectedAgg.delta)}</TableCell>
                      <TableCell align="right" sx={impactSx(selectedAgg.price)}>{fmtMoney0(selectedAgg.price)}</TableCell>
                      <TableCell align="right" sx={impactSx(selectedAgg.volmix)}>{fmtMoney0(selectedAgg.volmix)}</TableCell>
                      <TableCell align="right" sx={impactSx(selectedAgg.cogs)}>{fmtMoney0(selectedAgg.cogs)}</TableCell>
                      <TableCell align="right">{fmtQty(selectedAgg.q0)}</TableCell>
                      <TableCell align="right">{fmtQty(selectedAgg.q1)}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  )}

                  {!filteredRows.length && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Box sx={{ py: 4, color: 'text.secondary' }}>
                          No results
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>

        {this.state.busyOverlay && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(15, 23, 42, 0.16)',
              backgroundImage:
                'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.18) 75%, rgba(15, 23, 42, 0.22) 100%)',
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: '14px',
                bgcolor: 'background.paper',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <CircularProgress size={22} color="inherit" />
              <Typography variant="body2">Preparing report…</Typography>
            </Paper>
          </Box>
        )}
      </Paper>
    );
  }
}
